import { hexColorByName } from "../color-names";
import type { ColorValue } from "../color-value";
import { isColorValue } from "../color-value";
import type { HexColor } from "../hex-color/hex-color";
import { parseCssHex } from "../parce-css-hex";
import type { RgbaArray } from "../rgba-color/rgba-array";
import { hslaToRgba } from "./hsla-to-rgba";

const colorToRgbaArray = (
    color: HexColor | string | RgbaArray | ColorValue,
): RgbaArray => {
    if (Array.isArray(color)) {
        if (color.length < 3 || color.length > 4)
            throw new Error(
                "Invalid color format; must be an array of 3 or 4 numbers",
            );
        return [
            color[0],
            color[1],
            color[2],
            color[3] !== undefined ? color[3] : 1,
        ] as RgbaArray;
    }

    if (isColorValue(color)) {
        const rgbaColor = parseCssHex(color.asHex);
        return [
            rgbaColor.red,
            rgbaColor.green,
            rgbaColor.blue,
            rgbaColor.alpha,
        ];
    }

    // Check the format of the color, HEX or RGB or HSL?
    if (color.match(/^rgb/)) {
        // If RGB --> store the red, green, blue values in separate variables

        const colorMatches = color.match(
            /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/,
        );
        if (colorMatches === null) {
            throw new Error("Invalid color format");
        }
        return colorMatches.map((match: string) => Number(match)) as RgbaArray;
    } else if (color.match(/^hsl/)) {
        // If hsl --> store the red, green, blue values in separate variables
        // \s*([\d.]+)
        const colorMatches = color.match(
            /^hsla?\((\d+),\s*([\d.]+)%,\s*([\d.]+)%,(?:,\s*(\d+(?:\.\d+)?))?\)$/g,
        );
        if (colorMatches === null) {
            throw new Error("Invalid color format");
        }
        const hue = Number(colorMatches[1]);
        const saturation = Number(colorMatches[2]) / 100;
        const light = Number(colorMatches[3]) / 100;
        const alpha = colorMatches[4] ? Number(colorMatches[4]) : 1;
        const rgba = hslaToRgba(hue, saturation, light, alpha);
        return [rgba.red, rgba.green, rgba.blue, rgba.alpha] as RgbaArray;
    } else {
        // Check if the colors i a color name, if so, convert to hex
        const hexColor = (
            hexColorByName.has(color) ? hexColorByName.get(color) : color
        ) as HexColor;

        const { red, green, blue, alpha } = parseCssHex(hexColor);
        return [red, green, blue, alpha] as RgbaArray;
    }
};

export { colorToRgbaArray };
